import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalConfig } from '../../../shared/confirmation-modal/confirmation-modal-config';
import { Router } from '@angular/router';
import { MenuStore } from '../../../menus/service/menu.store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, take, takeUntil, tap } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { MatchingAgencyService } from '../../../shared/matching-agency/matching-agency.service';
import { MatchingAgencyCandidate } from '../../models/matching-agency-candidate';
import { MatchingAgency } from '../../models/matching-agency';
import { Subject } from 'rxjs/Subject';
import { Payment } from '../../models/payment';
import { PaymentType } from '../../models/payment-type';
import { MatchingAgencyCandidateAssign } from '../../models/matching-agency-candidate-assign';
import { PropertyMatch } from '../../models/property-match';
import { MatchingAgencyAdd } from '../../models/matching-agency-add';
import { FlashMessagesService } from '../../../shared/flash-messages/service/flash.messages.service';

@Component({
  selector: 'pioneer-matching-agency-candidate',
  templateUrl: './matching-agency-candidate.component.html',
  styles: []
})
export class MatchingAgencyCandidateComponent implements OnInit, OnDestroy {

  agency: MatchingAgency;

  currentPage: number;

  candidatesForm: FormGroup;

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;

  confirmationModalConfig = new ConfirmationModalConfig();

  private _destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private matchingAgencyService: MatchingAgencyService,
    private router: Router,
    private menuStore: MenuStore,
    private translateService: TranslateService,
    private flashMessagesService: FlashMessagesService) {
  }

  ngOnInit() {
    this.menuStore.setPageHeader('EVENT.AGENCY_REVIEW.CANDIDATE_PAGE_TITLE', false);

    this.candidatesForm = this.formBuilder.group({
      'matchingOption': [ null,  ]
    });

    this.matchingAgencyService.selectedAgency$
      .pipe(takeUntil(this._destroy$))
      .subscribe((agency: MatchingAgency) => {
        this.agency = agency;
      });

    this.matchingAgencyService.selectedPage$
      .pipe(takeUntil(this._destroy$))
      .subscribe((page: number) => {
        this.currentPage = page;
      });

  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get matchingOption(): string {
    return this.getFormControlValue('matchingOption');
  }

  private getFormControlValue(name: string): any {
    return this.candidatesForm ? this.candidatesForm.get(name).value : null;
  }

  noOptionSelected(): boolean {
    return !this.matchingOption;
  }

  cancelMatch() {
    this.resetForm();
    this.matchingAgencyService.resetSelectedAgency();
    this.router.navigate([ '/events/matching/agency' ], {
    });
  }

  private resetForm(): void {
    this.candidatesForm = this.formBuilder.group({
      'matchingOption': [ null,  ]
    });
  }

  matchCandidate(): void {
    const assign = this.agency.candidates.find(candidate =>
      candidate.matchingAgencyCandidateId === Number(this.matchingOption));
    this.assignCandidate(assign);
  }

  assignCandidate(candidate: MatchingAgencyCandidate) {
    this.candidatesForm.disable();

    const request = new MatchingAgencyCandidateAssign({
      matchingAgencyId: this.agency.matchingAgencyId,
      matchingAgencyCandidateId: candidate.matchingAgencyCandidateId
    });

    this.matchingAgencyService.assignMatchingCandidate(request).pipe(
      take(1),
      tap(() => {
        this.refreshAgencies(this.currentPage);
      }),
      catchError(() => {
        return Observable.of(null);
      }),
      finalize(() => {
        this.candidatesForm.enable();
      })
    ).subscribe();
  }

  private refreshAgencies(page: number) {
    this.matchingAgencyService.refreshMatchingAgencies(page)
      .pipe(take(1))
      .subscribe(() => {
        this.cancelMatch();
      });
  }

  addNewAgency(): void {
    const request = new MatchingAgencyAdd({
      matchingAgencyId: this.agency.matchingAgencyId,
      taCode: this.agency.taCode,
      eventId: this.agency.eventId
    });
    try {
      this.matchingAgencyService.addAgency(request)
        .pipe(take(1))
        .subscribe(() => {
          this.refreshAgencies(this.currentPage);
        });
    } catch (error) {
      Observable.throw(error);
    }
  }

  confirmBeforeAddAgency(): void {
    // set up the title of the confirmation modal
    const label = 'EVENT.AGENCY_REVIEW.ADD_AGENCY_CONFIRMATION_TITLE';
    const confirmLabel = 'EVENT.AGENCY_REVIEW.CONFIRMATION_ADD';
    const params = {};
    const title = this.translateService.instant(label, params);
    const confirmTitle = this.translateService.instant(confirmLabel);
    this.confirmationModalConfig.title = title;
    this.confirmationModalConfig.confirm = confirmTitle;

    this.confirmationModal.open();
  }

  confirmBeforeAddAgencyHandler(value) {
    if (value) {
      this.addNewAgency();
    }
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/matching-agency/matching-agency-candidate/matching-agency-candidate.component.ts